/**
 * GridView.ts
 * Central pointer router and selection manager for the dungeon editor.
 *
 * Manages editor modes (select/walls/content/navigate) and routes
 * pointer events to the appropriate tool based on the active mode.
 *
 * NOTE: Babylon.js ArcRotateCamera uses Shift for panning, so we
 * use Ctrl+click for multi-select instead.
 */

import type { PointerInfo } from '@babylonjs/core'
import type { Engine } from '../core/Engine.ts'
import type { GridRenderer } from '../grid/GridRenderer.ts'
import type { GridPos } from '../types/OctileTypes.ts'

// ─── Types ──────────────────────────────────────────────────

export type EditorMode = 'select' | 'walls' | 'content' | 'navigate'

export const EDITOR_MODES: readonly EditorMode[] = ['select', 'walls', 'content', 'navigate'] as const

export const MODE_LABELS: Record<EditorMode, string> = {
    select: 'Select',
    walls: 'Walls',
    content: 'Content',
    navigate: 'Navigate',
}

export const MODE_TIPS: Record<EditorMode, string[]> = {
    select: [
        'Click a cell to select it',
        'Ctrl+click to add cells to selection',
        'Press SPACE to switch to Walls mode',
    ],
    walls: [
        'Hover over walls to highlight them',
        'Click a wall to cycle its state',
        'States: Solid → Door → Gate → Grate → Window → Secret → Open',
    ],
    content: [
        'Click a cell to place content',
        'Drag items from the palette (coming soon)',
        'Press SPACE to switch modes',
    ],
    navigate: [
        'Left-click + drag to orbit the camera',
        'Right-click + drag to pan',
        'Scroll wheel to zoom in/out',
    ],
}

export interface GridViewEvents {
    onCellSelected: (row: number, col: number) => void
    onCellDeselected: () => void
    onWallClicked: (row: number, col: number, wallIndex: number) => void
    onMultiSelectChanged: (cells: GridPos[]) => void
    onModeChanged: (mode: EditorMode) => void
}

// ─── GridView ───────────────────────────────────────────────

export class GridView {
    private engine: Engine
    private renderer: GridRenderer
    private events: GridViewEvents
    private mode: EditorMode = 'select'
    private selection: GridPos[] = []
    private enabled = true

    constructor(engine: Engine, renderer: GridRenderer, events: GridViewEvents) {
        this.engine = engine
        this.renderer = renderer
        this.events = events

        this.engine.setPointerCallbacks({
            onDown: (info) => this.handlePointerDown(info),
            onMove: (info) => this.handlePointerMove(info),
        })
    }

    // ─── Mode Management ───────────────────────────────

    getMode(): EditorMode { return this.mode }

    setMode(mode: EditorMode): void {
        this.mode = mode
        this.renderer.clearWallHighlight()

        // Update canvas cursor
        const canvas = this.engine.babylon.getRenderingCanvas()
        if (canvas) {
            const cursors: Record<EditorMode, string> = {
                select: 'default',
                walls: 'crosshair',
                content: 'grab',
                navigate: 'move',
            }
            canvas.style.cursor = cursors[mode]
        }

        this.events.onModeChanged(mode)
    }

    cycleMode(): void {
        const idx = EDITOR_MODES.indexOf(this.mode)
        const next = EDITOR_MODES[(idx + 1) % EDITOR_MODES.length]
        this.setMode(next)
    }

    setEnabled(enabled: boolean): void {
        this.enabled = enabled
    }

    // ─── Selection ─────────────────────────────────────

    getSelection(): GridPos[] { return [...this.selection] }

    selectCell(row: number, col: number): void {
        this.renderer.deselectAll()
        this.selection = [{ row, col }]
        this.renderer.selectCell(row, col)
        this.events.onCellSelected(row, col)
    }

    addToSelection(row: number, col: number): void {
        if (this.selection.find(p => p.row === row && p.col === col)) return
        this.selection.push({ row, col })
        this.renderer.selectCell(row, col)
        this.events.onMultiSelectChanged([...this.selection])
    }

    deselectAll(): void {
        this.renderer.deselectAll()
        this.selection = []
        this.events.onCellDeselected()
    }

    // ─── Pointer Handling ──────────────────────────────

    private handlePointerDown(info: PointerInfo): void {
        if (!this.enabled) return

        // Use manual scene.pick() for reliable hit detection
        const scene = this.engine.scene
        const pickResult = scene.pick(scene.pointerX, scene.pointerY)

        const meta = pickResult?.hit ? pickResult.pickedMesh?.metadata : null
        if (!meta || meta.gridRow === undefined || meta.gridCol === undefined) return

        const row: number = meta.gridRow
        const col: number = meta.gridCol
        const wallIndex: number | undefined = meta.wallIndex
        // Ctrl+click for multi-select (Shift is consumed by Babylon's camera panning)
        const ctrlHeld = !!(info.event as PointerEvent).ctrlKey || !!(info.event as PointerEvent).metaKey

        switch (this.mode) {
            case 'select':
                // Only respond to floor clicks (no wallIndex)
                if (wallIndex === undefined) {
                    if (ctrlHeld) {
                        this.addToSelection(row, col)
                    } else {
                        this.selectCell(row, col)
                    }
                }
                break

            case 'walls':
                // Respond to wall clicks (has wallIndex)
                if (wallIndex !== undefined) {
                    this.events.onWallClicked(row, col, wallIndex)
                }
                break

            case 'content':
                // Floor clicks for content placement
                if (wallIndex === undefined) {
                    this.events.onCellSelected(row, col)
                }
                break

            case 'navigate':
                // No pick handling — camera orbit only
                break
        }
    }

    private handlePointerMove(info: PointerInfo): void {
        if (!this.enabled) return

        // Wall hover highlighting only in walls mode
        if (this.mode !== 'walls') {
            this.renderer.clearWallHighlight()
            return
        }

        // Manual scene.pick() for reliable hover detection
        // (Babylon's auto-pick on move can be unreliable)
        const scene = this.engine.scene
        const pickResult = scene.pick(scene.pointerX, scene.pointerY)

        const meta = pickResult?.hit ? pickResult.pickedMesh?.metadata : null
        if (meta && meta.wallIndex !== undefined && meta.gridRow !== undefined) {
            this.renderer.highlightWall(meta.gridRow, meta.gridCol, meta.wallIndex)
        } else {
            this.renderer.clearWallHighlight()
        }
    }

    // ─── Cleanup ───────────────────────────────────────

    dispose(): void {
        this.renderer.clearWallHighlight()
        this.deselectAll()
    }
}
